import { SAVED_FLYTHROUGH_HISTORY_LIMIT } from './flythroughVideoConfig';

function storageKey(roomId, roomImageId) {
  return `photorealistic:local-flythroughs:${String(roomId)}:${String(roomImageId)}`;
}

function getFlythroughResultSortTime(entry) {
  const submittedAt = entry?.submittedAt ?? entry?.submitted_at;
  if (submittedAt != null) {
    const submitted = Number(submittedAt);
    if (Number.isFinite(submitted)) return submitted;
  }

  const value =
    entry?.completedAt ??
    entry?.completed_at ??
    entry?.startedAt ??
    entry?.started_at ??
    entry?.createdAt ??
    entry?.created_at ??
    null;
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function normalizeEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;

  const resultVideoS3Key =
    entry.resultVideoS3Key ??
    entry.result_video_s3_key ??
    entry.resultS3Key ??
    entry.result_s3_key ??
    '';
  const thumbnailS3Key = entry.thumbnailS3Key ?? entry.thumbnail_s3_key ?? '';
  const payload = entry.payload ?? null;
  const createdAt = entry.createdAt ?? entry.created_at ?? null;
  const startedAt = entry.startedAt ?? entry.started_at ?? null;
  const completedAt = entry.completedAt ?? entry.completed_at ?? null;
  const taskId = entry.taskId ?? entry.task_id ?? null;
  const inferenceId = entry.inferenceId ?? entry.inference_id ?? entry.id ?? null;
  const id =
    entry.id ??
    taskId ??
    (inferenceId != null ? String(inferenceId) : null) ??
    (completedAt ? `flythrough-${completedAt}` : createdAt ? `flythrough-${createdAt}` : null);

  if (!id || !resultVideoS3Key || !payload) return null;

  return {
    id: String(id),
    taskId: taskId != null ? String(taskId) : null,
    inferenceId: inferenceId != null ? String(inferenceId) : null,
    resultVideoS3Key: String(resultVideoS3Key).trim(),
    thumbnailS3Key: thumbnailS3Key ? String(thumbnailS3Key).trim() : '',
    payload,
    flythroughSettings: entry.flythroughSettings ?? null,
    submittedAt: entry.submittedAt ?? entry.submitted_at ?? null,
    createdAt,
    startedAt,
    completedAt,
  };
}

function sortNewestFirst(items) {
  return [...items].sort((a, b) => {
    const ta = getFlythroughResultSortTime(a);
    const tb = getFlythroughResultSortTime(b);
    if (tb !== ta) return tb - ta;
    return String(b?.id ?? '').localeCompare(String(a?.id ?? ''));
  });
}

export function loadLocalFlythroughResults(roomId, roomImageId) {
  if (roomId == null || roomImageId == null) return [];
  try {
    const raw = localStorage.getItem(storageKey(roomId, roomImageId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sortNewestFirst(parsed.map(normalizeEntry).filter(Boolean));
  } catch {
    return [];
  }
}

export function saveLocalFlythroughResults(roomId, roomImageId, items) {
  if (roomId == null || roomImageId == null) return;
  try {
    const normalized = sortNewestFirst(items.map(normalizeEntry).filter(Boolean)).slice(
      0,
      SAVED_FLYTHROUGH_HISTORY_LIMIT,
    );
    if (normalized.length === 0) {
      localStorage.removeItem(storageKey(roomId, roomImageId));
    } else {
      localStorage.setItem(storageKey(roomId, roomImageId), JSON.stringify(normalized));
    }
  } catch (err) {
    console.error('Failed to save local flythrough results', err);
  }
}

export function appendLocalFlythroughResult(roomId, roomImageId, entry) {
  const normalized = normalizeEntry(entry);
  if (!normalized || roomId == null || roomImageId == null) return null;

  const existing = loadLocalFlythroughResults(roomId, roomImageId).filter(
    (item) => item.id !== normalized.id,
  );
  const next = sortNewestFirst([normalized, ...existing]).slice(0, SAVED_FLYTHROUGH_HISTORY_LIMIT);
  saveLocalFlythroughResults(roomId, roomImageId, next);
  return normalized;
}

export function removeLocalFlythroughResult(roomId, roomImageId, entryId) {
  if (roomId == null || roomImageId == null || entryId == null) return;
  const next = loadLocalFlythroughResults(roomId, roomImageId).filter(
    (item) => String(item.id) !== String(entryId),
  );
  saveLocalFlythroughResults(roomId, roomImageId, next);
}
